import React from "react";
import { SCENARIOS, SEVERITY_LABELS } from "../data/scenarios";
import { ArrowCounterClockwise, Receipt, Play, Pause, GraduationCap } from "@phosphor-icons/react";

const STATUS_TONE = {
  low: "bg-stone-100 text-stone-700 border-stone-200",
  elevated: "bg-amber-50 text-amber-800 border-amber-200",
  high: "bg-orange-50 text-orange-800 border-orange-200",
  critical: "bg-rose-50 text-rose-800 border-rose-200",
};

export const Header = ({
  scenarioId,
  setScenarioId,
  severity,
  setSeverity,
  onReset,
  onOpenReceipts,
  receiptCount,
  onRunFlow,
  isReplaying,
  onToggleReplay,
  replayProgress,
  onStartTour,
}) => {
  const sev = SEVERITY_LABELS[severity];
  const fillPct = ((severity - 1) / 3) * 100;

  return (
    <header className="h-16 border-b border-stone-200 bg-white flex items-center justify-between px-6 shrink-0">
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-sm bg-stone-900 flex items-center justify-center">
            <span className="font-serif italic text-white text-base leading-none">Ai</span>
          </div>
          <div className="flex flex-col leading-tight">
            <span className="text-[11px] uppercase tracking-[0.18em] text-stone-500 font-medium">
              AICierge
            </span>
            <span className="text-sm text-stone-900 font-medium">
              Governed AI operations <span className="text-stone-300">·</span> policy gates{" "}
              <span className="text-stone-300">·</span> signed receipts
            </span>
          </div>
        </div>

        <div className="hidden md:flex items-center gap-2 pl-4 border-l border-stone-200">
          <span className="text-[11px] uppercase tracking-wider text-stone-500">Scenario</span>
          <select
            data-testid="scenario-picker"
            value={scenarioId}
            onChange={(e) => setScenarioId(e.target.value)}
            className="text-sm bg-white border border-stone-200 rounded-sm px-2 py-1.5 hover:border-stone-300 focus:outline-none focus:border-stone-900 focus:ring-1 focus:ring-stone-900"
          >
            {SCENARIOS.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex items-center gap-3">
          <span className="text-[11px] uppercase tracking-wider text-stone-500">Severity</span>
          <div
            data-testid="severity-display"
            className={`text-[11px] font-mono px-2 py-0.5 rounded-sm border ${STATUS_TONE[sev.tone]}`}
          >
            {severity}/4 · {sev.label}
          </div>
          <input
            data-testid="severity-slider"
            type="range"
            min={1}
            max={4}
            step={1}
            value={severity}
            onChange={(e) => setSeverity(Number(e.target.value))}
            className="sev-slider w-40"
            style={{ "--fill": `${fillPct}%` }}
          />
        </div>

        <div className="hidden lg:flex items-center gap-1.5 pl-4 border-l border-stone-200">
          <span className="text-[11px] uppercase tracking-wider text-stone-500 mr-1">Demo flow</span>
          <FlowBtn label="Low" onClick={() => onRunFlow("low")} testid="flow-low" />
          <FlowBtn label="Escalating" onClick={() => onRunFlow("escalating")} testid="flow-escalating" />
          <FlowBtn label="Sensitive" onClick={() => onRunFlow("sensitive")} testid="flow-sensitive" />
        </div>

        <div className="hidden xl:flex items-center pl-4 border-l border-stone-200">
          <button
            data-testid="toggle-replay"
            onClick={onToggleReplay}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-sm transition-colors ${
              isReplaying
                ? "bg-stone-900 text-white border-stone-900 hover:bg-stone-800"
                : "border-stone-200 hover:bg-stone-50"
            }`}
          >
            {isReplaying ? <Pause size={13} weight="fill" /> : <Play size={13} weight="fill" />}
            {isReplaying ? "Pause replay" : "Timeline replay"}
            {isReplaying && (
              <span className="ml-1 w-12 h-1 bg-white/30 rounded-full overflow-hidden">
                <span
                  className="block h-full bg-white"
                  style={{ width: `${Math.round(replayProgress * 100)}%` }}
                />
              </span>
            )}
          </button>
        </div>

        <button
          data-testid="start-tour"
          onClick={onStartTour}
          className="hidden md:flex items-center gap-1.5 px-3 py-1.5 text-sm border border-stone-200 rounded-sm hover:bg-stone-50 transition-colors"
        >
          <GraduationCap size={14} />
          Walkthrough
        </button>

      <button
        data-testid="open-receipts"
        onClick={onOpenReceipts}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-stone-200 rounded-sm hover:bg-stone-50 transition-colors"
      >
        <Receipt size={14} weight="regular" />
        Decision records
        <span className="font-mono text-[10px] bg-stone-900 text-white px-1.5 py-0.5 rounded-sm">
          {receiptCount}
        </span>
      </button>

        <button
          data-testid="reset-demo"
          onClick={onReset}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-stone-600 hover:bg-stone-100 rounded-sm transition-colors"
        >
          <ArrowCounterClockwise size={14} />
          Reset
        </button>
      </div>
    </header>
  );
};

const FlowBtn = ({ label, onClick, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium border border-stone-200 rounded-sm hover:bg-stone-50 hover:border-stone-300 transition-colors"
  >
    <Play size={10} weight="fill" className="text-stone-500" />
    {label}
  </button>
);
